import axios from "axios";
import api from "./axiosConfig";
import { extractApiErrorMessage } from "./preppoCertificateUpload";

export const MAX_AGENT_CV_SIZE_BYTES = 20 * 1024 * 1024;
export const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export const ACCEPTED_CV_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
}

export function resolveCvMimeType(file: File): string | null {
  if (file.type && ACCEPTED_CV_MIME_TYPES.includes(file.type)) {
    return file.type;
  }
  return MIME_BY_EXTENSION[getFileExtension(file.name)] || null;
}

async function verifyPendingObjectExists(objectKey: string): Promise<boolean> {
  try {
    const response = await api.post("/agents/pending-upload/verify", {
      objectKey,
    });
    return Boolean(response.data?.exists);
  } catch {
    return false;
  }
}

async function postFileToPresignedUrl(
  uploadUrl: string,
  fields: Record<string, string>,
  file: File,
  objectKey: string
): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  formData.append("file", file);

  try {
    await axios.post(uploadUrl, formData, {
      timeout: UPLOAD_TIMEOUT_MS,
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers === "object") {
            delete (headers as Record<string, unknown>)["Content-Type"];
          }
          return data;
        },
      ],
      validateStatus: (status) => status === 201 || status === 204,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new Error("Arquivo excede o limite de 20MB.");
    }
    if (axios.isAxiosError(error) && !error.response) {
      if (await verifyPendingObjectExists(objectKey)) {
        return;
      }
    }
    throw error;
  }
}

/** Presigned POST no S3 durante o cadastro (antes do agent existir). */
export async function uploadPendingAgentCv(file: File): Promise<string> {
  const mimeType = resolveCvMimeType(file);
  if (!mimeType) {
    throw new Error("Tipo de arquivo inválido. Envie PDF, DOC ou DOCX.");
  }
  if (file.size > MAX_AGENT_CV_SIZE_BYTES) {
    throw new Error("Arquivo excede o limite de 20MB.");
  }

  try {
    const initResponse = await api.post("/agents/cv/upload-url", {
      fileName: file.name,
      mimeType,
      size: file.size,
    });

    const { uploadUrl, fields, objectKey } = initResponse.data as {
      uploadUrl: string;
      fields: Record<string, string>;
      objectKey: string;
    };

    await postFileToPresignedUrl(uploadUrl, fields, file, objectKey);

    return objectKey;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new Error("Arquivo excede o limite de 20MB.");
    }
    throw new Error(
      extractApiErrorMessage(
        error,
        "Erro ao enviar currículo. Tente novamente."
      )
    );
  }
}
